import { FormEvent, useMemo, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { getApiErrorMessage, portalApi } from '../../api/client';
import type { KybDocumentType } from '../../api/types';
import { KybDocumentUpload } from '../../components/kyb/KybDocumentUpload';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { useAuth } from '../../context/AuthContext';
import { readFileAsBase64 } from '../../utils/format';

const DOC_TYPES: { type: KybDocumentType; label: string }[] = [
  { type: 'trade_license', label: 'Trade License' },
  { type: 'passport', label: 'Passport / ID (UBO)' },
  { type: 'incorporation_doc', label: 'Certificate of Incorporation' },
];

const ALLOWED = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
const MAX = 10 * 1024 * 1024;

export function KybWizardPage() {
  const { merchant, kybDocuments, refresh } = useAuth();
  const step = Math.max(1, merchant?.kyb_step ?? 0) >= 3 ? 3 : Math.max(1, (merchant?.kyb_step ?? 0) + 1);
  const [active, setActive] = useState(step > 3 ? 3 : step);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const [corporate, setCorporate] = useState({
    company_name: merchant?.name ?? '',
    legal_address: merchant?.legal_address ?? '',
    registration_country: merchant?.registration_country ?? '',
    trade_license_number: merchant?.trade_license_number ?? '',
    license_expiry_date: merchant?.license_expiry_date ?? '',
    corporate_structure: merchant?.corporate_structure ?? 'LLC',
  });
  const [ubo, setUbo] = useState({
    full_name: merchant?.ubo_full_name ?? '',
    ubo_email: merchant?.ubo_email ?? '',
    phone: merchant?.ubo_phone ?? '',
    nationality: merchant?.ubo_nationality ?? '',
  });

  const uploadedCount = useMemo(
    () => DOC_TYPES.filter((d) => kybDocuments[d.type]?.uploaded).length,
    [kybDocuments],
  );
  const allDocsUploaded = uploadedCount === DOC_TYPES.length;

  const saveCorporate = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErr('');
    try {
      await portalApi.kybCorporate(corporate);
      await refresh();
      setActive(2);
      setMsg('Corporate info saved');
    } catch (ex) {
      setErr(getApiErrorMessage(ex));
    } finally {
      setLoading(false);
    }
  };

  const saveUbo = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await portalApi.kybUbo(ubo);
      await refresh();
      setActive(3);
      setMsg('UBO details saved');
    } catch (ex) {
      setErr(getApiErrorMessage(ex));
    } finally {
      setLoading(false);
    }
  };

  const uploadDoc = async (docType: KybDocumentType, file: File) => {
    if (!ALLOWED.includes(file.type)) throw new Error('Only PDF, JPEG, PNG allowed');
    if (file.size > MAX) throw new Error('Max file size 10MB');
    const { base64, mime } = await readFileAsBase64(file);
    await portalApi.kybDocument({
      document_type: docType,
      mime_type: mime,
      file_base64: base64,
      file_name: file.name,
    });
    await refresh();
    setMsg(`${DOC_TYPES.find((d) => d.type === docType)?.label ?? 'Document'} saved on file`);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h2 className="font-display text-2xl">KYB onboarding</h2>
      <div className="flex gap-2 text-xs">
        {[1, 2, 3].map((s) => (
          <span
            key={s}
            className={`flex-1 text-center py-2 rounded-lg border ${
              active === s ? 'border-onyx-gold text-onyx-gold' : 'border-onyx-border text-onyx-muted'
            }`}
          >
            Step {s}
          </span>
        ))}
      </div>
      {msg ? <p className="text-onyx-success text-sm">{msg}</p> : null}
      {err ? <p className="text-onyx-danger text-sm">{err}</p> : null}

      {active === 1 && (
        <Card title="Corporate information">
          <form onSubmit={saveCorporate} className="space-y-4">
            <Input placeholder="Company name" value={corporate.company_name} onChange={(e) => setCorporate({ ...corporate, company_name: e.target.value })} required />
            <Input placeholder="Legal address" value={corporate.legal_address} onChange={(e) => setCorporate({ ...corporate, legal_address: e.target.value })} required />
            <Input placeholder="Country of registration" value={corporate.registration_country} onChange={(e) => setCorporate({ ...corporate, registration_country: e.target.value })} required />
            <Input placeholder="Trade license number" value={corporate.trade_license_number} onChange={(e) => setCorporate({ ...corporate, trade_license_number: e.target.value })} required />
            <Input type="date" placeholder="License expiry" value={corporate.license_expiry_date} onChange={(e) => setCorporate({ ...corporate, license_expiry_date: e.target.value })} required />
            <select
              className="input-field"
              value={corporate.corporate_structure}
              onChange={(e) => setCorporate({ ...corporate, corporate_structure: e.target.value })}
            >
              <option value="LLC">LLC</option>
              <option value="Freezone">Freezone</option>
              <option value="FZ-LLC">FZ-LLC</option>
              <option value="Branch">Branch</option>
            </select>
            <Button type="submit" disabled={loading}>Continue</Button>
          </form>
        </Card>
      )}

      {active === 2 && (
        <Card title="UBO / Director">
          <form onSubmit={saveUbo} className="space-y-4">
            <Input placeholder="Full name" value={ubo.full_name} onChange={(e) => setUbo({ ...ubo, full_name: e.target.value })} required />
            <Input type="email" placeholder="Email" value={ubo.ubo_email} onChange={(e) => setUbo({ ...ubo, ubo_email: e.target.value })} required />
            <Input placeholder="Phone" value={ubo.phone} onChange={(e) => setUbo({ ...ubo, phone: e.target.value })} required />
            <Input placeholder="Nationality" value={ubo.nationality} onChange={(e) => setUbo({ ...ubo, nationality: e.target.value })} required />
            <Button type="submit" disabled={loading}>Continue</Button>
          </form>
        </Card>
      )}

      {active === 3 && (
        <Card title="Document vault">
          <p className="text-sm text-onyx-muted mb-2">PDF, JPEG or PNG — max 10MB each</p>
          <p className="text-xs text-onyx-muted mb-4">
            {uploadedCount} of {DOC_TYPES.length} documents on file
          </p>

          {allDocsUploaded ? (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-onyx-success/40 bg-onyx-success/10 px-3 py-2 text-sm text-onyx-success">
              <CheckCircle2 size={18} aria-hidden />
              All required documents submitted. Compliance will review your application.
            </div>
          ) : null}

          <ul className="space-y-4">
            {DOC_TYPES.map((d) => (
              <KybDocumentUpload
                key={d.type}
                docType={d.type}
                label={d.label}
                status={kybDocuments[d.type]}
                disabled={loading}
                onUpload={(file) => uploadDoc(d.type, file)}
              />
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
